import { Icon } from "@/components/icons/Icon";
import { literals } from "@/constants/literals";
import { requestImageDeletion } from "@/lib/services/images";
import { deletePhoto } from "@/lib/services/orders";
import { cn } from "@/lib/styles.utils";
import { useLoader } from "@/store/useLoader";
import { useEffect, useRef, useState } from "react";
import DeletePhoto from "./DeletePhoto";
import { useUserStore } from "@/store/useUser";

type ActionItem = {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  dataTestid: string;
  disabled?: boolean;
};

const ButtonActionsGroup = ({
  photoDetail,
  photoURL,
  onClose,
  edit,
  openEditionRequest,
  disabledButtons,
}: {
  photoURL: string;
  photoDetail: any;
  onClose: () => void;
  edit: () => void;
  openEditionRequest: () => void;
  disabledButtons?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { upLoader, downLoader } = useLoader();
  const { isSupervisor } = useUserStore();

  const handleDownload = () => {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = photoURL;
    document.body.appendChild(iframe);

    setTimeout(() => iframe.remove(), 30_000);
  };

  const handleDelete = async () => {
    upLoader();
    const imageId = photoDetail.imageId;
    if (!imageId || disabledButtons) return;

    if (isSupervisor) {
      await deletePhoto(imageId);
    } else {
      await requestImageDeletion(imageId);
    }
    await onClose();
    downLoader();
  };

  const startEditionRequest = () => {
    if (!disabledButtons) {
      openEditionRequest();
    }
  };

  const items = [
    {
      dataTestid: "option-download-photo-info",
      label: literals.download,
      onClick: handleDownload,
      icon: <Icon name="download" />,
      hidden: !photoURL,
    },
    {
      dataTestid: "edit-photo-info",
      label: literals.edit,
      onClick: edit,
      icon: <Icon name="pencil" />,
      hidden: !isSupervisor,
    },
    {
      dataTestid: "request-edit-photo-info",
      label: literals.requestEdition,
      onClick: startEditionRequest,
      icon: <Icon name="pencil" />,
      hidden: isSupervisor,
      disabled: disabledButtons,
    },
  ].filter((item) => !item.hidden) as ActionItem[]; // filter out hidden items

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleItemClick = (item: ActionItem) => {
    item.onClick();
    setOpen(false);
  };

  return (
    <div
      data-testid="button-actions"
      ref={rootRef}
      className="relative inline-block"
    >
      <div className="flex overflow-hidden rounded-sm shadow-sm">
        <button
          data-testid="download-photo-info"
          type="button"
          onClick={handleDownload}
          className="flex h-11 items-center gap-2 bg-ib-red-main px-6 text-base font-medium text-white hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-400"
        >
          <Icon name="download" />
          <span>{literals.download}</span>
        </button>
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
          className="flex h-11 w-14 items-center justify-center border-l border-white/70 bg-ib-red-main text-white hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-400"
        >
          <Icon name="chevronDown" />
        </button>
      </div>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 w-56 overflow-hidden rounded-sm border border-gray-300 bg-white shadow-lg"
        >
          {items.map((item, index) => (
            <button
              data-testid={item.dataTestid}
              key={`${item.label}-${index}`}
              type="button"
              role="menuitem"
              onClick={() => handleItemClick(item)}
              className={cn(
                "flex w-full items-center gap-3 border-b border-gray-200 px-5 py-4 text-left text-[15px]",
                "last:border-b-0 text-gray-800 hover:bg-gray-50",
                item.disabled ? "cursor-not-allowed text-gray-400" : "",
              )}
              disabled={item.disabled}
            >
              <span className="shrink-0">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
          <DeletePhoto
            imageURL={photoURL}
            onDelete={handleDelete}
            confirmText={
              isSupervisor
                ? literals.confirmDeletePhoto
                : literals.confirmRequestDeletePhoto
            }
            disabled={disabledButtons}
          />
        </div>
      )}
    </div>
  );
};

export default ButtonActionsGroup;
